/**
 * A controller for managing dependent checkbox relationships where one checkbox controls
 * the enabled/disabled state of other checkboxes.
 *
 * @example Basic Usage
 * ```html
 * <div data-controller="dependent-checkbox" data-dependent-checkbox-disabled-class="opacity-50">
 *   <!-- Primary checkbox that controls the dependent ones -->
 *   <input type="checkbox" data-action="change->dependent-checkbox#toggle">
 *
 *   <!-- Dependent checkbox group -->
 *   <label data-dependent-checkbox-target="dependentLabel">
 *     <input type="checkbox" data-dependent-checkbox-target="dependent" disabled>
 *     Dependent Option
 *   </label>
 * </div>
 * ```
 *
 * @example With Default Disabled State
 * ```html
 * <div data-controller="dependent-checkbox"
 *      data-dependent-checkbox-disabled-class="opacity-50"
 *      data-dependent-checkbox-disabled-by-default-value="true">
 *   <!-- ... checkboxes ... -->
 * </div>
 * ```
 *
 * Targets:
 * - `dependent`: Checkbox inputs that are controlled by the primary checkbox
 * - `dependentLabel`: Labels or containers that should receive visual feedback (disabled class)
 *
 * Classes:
 * - `disabled`: Class to apply to dependentLabel targets when disabled (e.g. "opacity-50")
 *
 * Values:
 * - `disabledByDefault`: Boolean - whether dependent checkboxes should start disabled
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["dependent", "dependentLabel"];
  static classes = ["disabled"];
  static values = {
    disabledByDefault: Boolean,
  };

  connect() {
    if (this.hasDisabledByDefaultValue && this.disabledByDefaultValue) {
      this.disable();
    }
  }

  toggle(event) {
    if (event.target.checked) {
      this.enable();
    } else {
      this.disable();
    }
  }

  disable() {
    this.dependentTargets.forEach((dependent, index) => {
      dependent.disabled = true;
      dependent.checked = false;
    });

    this.dependentLabelTargets.forEach((label) => {
      label.classList.add(this.disabledClass);
    });
  }

  enable() {
    this.dependentTargets.forEach((dependent) => {
      dependent.disabled = false;
    });

    this.dependentLabelTargets.forEach((label) => {
      label.classList.remove(this.disabledClass);
    });
  }
}
